import React from 'react';

/* ═══════════════════════════════════════════════════════════════
   BadgeArt — Game-style SVG badge artwork for Eye Fitness

   Design system inspired by PlayStation trophies, Xbox achievements,
   Duolingo badges, and Apple Fitness rings.

   Two independent visual axes:
   1. SHAPE  — tells the user WHAT category the badge belongs to
   2. TIER   — tells the user how RARE/HARD the badge is

   Category → Shape:
     Milestone  → Medal (circle + ribbons)
     Streak     → Shield / crest
     Sessions   → Hexagon
     Points     → Star-burst circle
     Special    → Diamond gem
     Vision     → Eye circle

   Tier → Metallic rim color:
     Bronze   → Warm copper      (starter achievements)
     Silver   → Cool steel       (moderate achievements)
     Gold     → Rich gold        (hard achievements)
     Platinum → Blue-white        (very hard achievements)
     Diamond  → Icy crystal blue (elite achievements)
   ═══════════════════════════════════════════════════════════════ */

/* ── Tier metallic palettes ── */
const TIERS = {
    bronze:   { r1: '#CD7F32', r2: '#8B5A2B', glow: 'rgba(205,127,50,0.4)',  star: '#CD7F32' },
    silver:   { r1: '#D4D4D8', r2: '#9CA3AF', glow: 'rgba(156,163,175,0.4)', star: '#A8A8B0' },
    gold:     { r1: '#FFD700', r2: '#B8860B', glow: 'rgba(255,215,0,0.5)',   star: '#FFD700' },
    platinum: { r1: '#E2E8F0', r2: '#94A3B8', glow: 'rgba(148,163,184,0.5)', star: '#CBD5E1' },
    diamond:  { r1: '#B9F2FF', r2: '#38BDF8', glow: 'rgba(56,189,248,0.6)',  star: '#7DD3FC' },
};

/* ── Badge definitions: shape + fill colors + tier ── */
const BADGE_META = {
    // ── Milestone ──
    first_session:    { shape: 'medal',     g1: '#60A5FA', g2: '#2563EB', tier: 'bronze' },

    // ── Streak (shields, tier escalates with duration) ──
    '3_day_streak':   { shape: 'shield',    g1: '#FB923C', g2: '#EA580C', tier: 'bronze' },
    '7_day_streak':   { shape: 'shield',    g1: '#FBBF24', g2: '#D97706', tier: 'silver' },
    '14_day_streak':  { shape: 'shield',    g1: '#F59E0B', g2: '#B45309', tier: 'gold' },
    '30_day_streak':  { shape: 'shield',    g1: '#FCD34D', g2: '#CA8A04', tier: 'platinum' },
    '60_day_streak':  { shape: 'shield',    g1: '#67E8F9', g2: '#0891B2', tier: 'diamond' },
    '365_day_streak': { shape: 'shield',    g1: '#FDE68A', g2: '#B45309', tier: 'diamond' },

    // ── Sessions (hexagons, tier escalates with count) ──
    '10_sessions':    { shape: 'hex',       g1: '#6EE7B7', g2: '#059669', tier: 'bronze' },
    '25_sessions':    { shape: 'hex',       g1: '#34D399', g2: '#047857', tier: 'silver' },
    '50_sessions':    { shape: 'hex',       g1: '#10B981', g2: '#065F46', tier: 'gold' },
    '100_sessions':   { shape: 'hex',       g1: '#6EE7B7', g2: '#059669', tier: 'platinum' },
    '200_sessions':   { shape: 'hex',       g1: '#FDE68A', g2: '#B45309', tier: 'diamond' },

    // ── Points (starbursts, tier escalates with score) ──
    '100_points':     { shape: 'starburst', g1: '#C084FC', g2: '#7C3AED', tier: 'bronze' },
    '1000_points':    { shape: 'starburst', g1: '#A78BFA', g2: '#6D28D9', tier: 'gold' },
    '5000_points':    { shape: 'starburst', g1: '#FDE68A', g2: '#B45309', tier: 'diamond' },

    // ── Special / variety (diamonds) ──
    explorer:         { shape: 'diamond',   g1: '#5EEAD4', g2: '#0D9488', tier: 'gold' },
    iron_eyes:        { shape: 'diamond',   g1: '#94A3B8', g2: '#475569', tier: 'platinum' },
    pattern_pro:      { shape: 'diamond',   g1: '#7DD3FC', g2: '#0284C7', tier: 'gold' },
    level_up:         { shape: 'diamond',   g1: '#86EFAC', g2: '#16A34A', tier: 'silver' },
    overachiever:     { shape: 'diamond',   g1: '#FDBA74', g2: '#C2410C', tier: 'silver' },
    hat_trick:        { shape: 'diamond',   g1: '#D8B4FE', g2: '#7C3AED', tier: 'gold' },
    comeback_kid:     { shape: 'diamond',   g1: '#93C5FD', g2: '#1D4ED8', tier: 'silver' },
    weekend_warrior:  { shape: 'diamond',   g1: '#FCA5A5', g2: '#DC2626', tier: 'silver' },

    // ── Vision test (eye circles) ──
    eye_curious:      { shape: 'eye',       g1: '#A5B4FC', g2: '#4338CA', tier: 'bronze' },
    progress_watcher: { shape: 'eye',       g1: '#818CF8', g2: '#4338CA', tier: 'silver' },
    perfect_vision:   { shape: 'eye',       g1: '#FDE68A', g2: '#B45309', tier: 'diamond' },
};

const LOCKED_FILL = { g1: '#4B5563', g2: '#1F2937' };
const LOCKED_RIM  = { r1: '#374151', r2: '#1F2937' };

/* ═══════════════════════════════════════════════
   Shape components (viewBox 0 0 64 64)
   Each shape features:
   - Outer form with gradient fill
   - Inner lighter inset for depth
   - Specular highlight (upper-left shine)
   - Drop shadow via filter
   ═══════════════════════════════════════════════ */

function MedalShape({ gid, rid }) {
    return (
        <g>
            {/* Ribbons */}
            <polygon points="22,46 16,63 30,53" fill={`url(#${rid})`} opacity="0.55"/>
            <polygon points="42,46 48,63 34,53" fill={`url(#${rid})`} opacity="0.55"/>
            {/* Outer ring */}
            <circle cx="32" cy="28" r="25" fill={`url(#${gid})`} stroke={`url(#${rid})`} strokeWidth="3"/>
            {/* Inner ring */}
            <circle cx="32" cy="28" r="20" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5"/>
            {/* Depth ring */}
            <circle cx="32" cy="28" r="20" fill="rgba(255,255,255,0.08)"/>
            {/* Top shine */}
            <ellipse cx="24" cy="18" rx="12" ry="8" fill="rgba(255,255,255,0.22)"/>
            {/* Bottom shadow */}
            <ellipse cx="32" cy="40" rx="14" ry="4" fill="rgba(0,0,0,0.08)"/>
        </g>
    );
}

function ShieldShape({ gid, rid }) {
    return (
        <g>
            {/* Shield body */}
            <path
                d="M32 2 L56 13 L52 44 Q32 60 32 60 Q32 60 12 44 L8 13 Z"
                fill={`url(#${gid})`} stroke={`url(#${rid})`} strokeWidth="2.5" strokeLinejoin="round"
            />
            {/* Inner shield */}
            <path
                d="M32 8 L50 17 L47 41 Q32 53 32 53 Q32 53 17 41 L14 17 Z"
                fill="rgba(255,255,255,0.08)"
            />
            {/* Center divider */}
            <line x1="32" y1="8" x2="32" y2="53" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5"/>
            {/* Top shine */}
            <ellipse cx="26" cy="16" rx="11" ry="6" fill="rgba(255,255,255,0.2)"/>
            {/* Bottom shadow */}
            <ellipse cx="32" cy="52" rx="10" ry="3" fill="rgba(0,0,0,0.06)"/>
        </g>
    );
}

function HexShape({ gid, rid }) {
    return (
        <g>
            {/* Outer hex */}
            <polygon
                points="32,2 58,17 58,47 32,62 6,47 6,17"
                fill={`url(#${gid})`} stroke={`url(#${rid})`} strokeWidth="2.5" strokeLinejoin="round"
            />
            {/* Inner hex */}
            <polygon
                points="32,9 51,20 51,44 32,55 13,44 13,20"
                fill="rgba(255,255,255,0.08)"
            />
            {/* Inner border */}
            <polygon
                points="32,9 51,20 51,44 32,55 13,44 13,20"
                fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1"
            />
            {/* Top shine */}
            <ellipse cx="26" cy="18" rx="13" ry="7" fill="rgba(255,255,255,0.18)"/>
        </g>
    );
}

function StarburstShape({ gid, rid }) {
    return (
        <g>
            {/* Outer circle */}
            <circle cx="32" cy="32" r="29" fill={`url(#${gid})`} stroke={`url(#${rid})`} strokeWidth="2.5"/>
            {/* 8-point star rays */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => {
                const rad = (angle - 90) * Math.PI / 180;
                const x1 = 32 + 18 * Math.cos(rad);
                const y1 = 32 + 18 * Math.sin(rad);
                const x2 = 32 + 27 * Math.cos(rad);
                const y2 = 32 + 27 * Math.sin(rad);
                return <line key={angle} x1={x1} y1={y1} x2={x2} y2={y2}
                             stroke="rgba(255,255,255,0.2)" strokeWidth="2" strokeLinecap="round"/>;
            })}
            {/* Inner disc */}
            <circle cx="32" cy="32" r="16" fill="rgba(255,255,255,0.1)"/>
            <circle cx="32" cy="32" r="16" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            {/* Top shine */}
            <ellipse cx="25" cy="22" rx="11" ry="7" fill="rgba(255,255,255,0.2)"/>
        </g>
    );
}

function DiamondShape({ gid, rid }) {
    return (
        <g>
            {/* Gem body */}
            <path
                d="M32 2 L60 32 L32 62 L4 32 Z"
                fill={`url(#${gid})`} stroke={`url(#${rid})`} strokeWidth="2.5" strokeLinejoin="round"
            />
            {/* Inner facet */}
            <path d="M32 10 L52 32 L32 54 L12 32 Z" fill="rgba(255,255,255,0.08)"/>
            {/* Facet lines */}
            <line x1="32" y1="2" x2="32" y2="62" stroke="rgba(255,255,255,0.08)" strokeWidth="0.7"/>
            <line x1="4" y1="32" x2="60" y2="32" stroke="rgba(255,255,255,0.08)" strokeWidth="0.7"/>
            {/* Crown facets */}
            <path d="M32 2 L52 32 L32 24 Z" fill="rgba(255,255,255,0.12)"/>
            <path d="M32 2 L12 32 L32 24 Z" fill="rgba(255,255,255,0.06)"/>
            {/* Top shine */}
            <ellipse cx="24" cy="20" rx="10" ry="8" fill="rgba(255,255,255,0.18)"/>
        </g>
    );
}

function EyeShape({ gid, rid }) {
    return (
        <g>
            {/* Outer circle */}
            <circle cx="32" cy="32" r="29" fill={`url(#${gid})`} stroke={`url(#${rid})`} strokeWidth="2.5"/>
            {/* Eye shape */}
            <path
                d="M4 32 Q32 12 60 32 Q32 52 4 32 Z"
                fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5"
            />
            {/* Iris ring */}
            <circle cx="32" cy="32" r="9" fill="rgba(0,0,0,0.15)" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5"/>
            {/* Pupil */}
            <circle cx="32" cy="32" r="4" fill="rgba(0,0,0,0.25)"/>
            {/* Eye shine */}
            <circle cx="35" cy="29" r="2" fill="rgba(255,255,255,0.4)"/>
            {/* Top shine */}
            <ellipse cx="25" cy="20" rx="11" ry="7" fill="rgba(255,255,255,0.18)"/>
        </g>
    );
}

const SHAPE_MAP = {
    medal: MedalShape,
    shield: ShieldShape,
    hex: HexShape,
    starburst: StarburstShape,
    diamond: DiamondShape,
    eye: EyeShape,
};

/* Emoji vertical center per shape */
const TEXT_Y = { medal: 28, shield: 31, hex: 32, starburst: 32, diamond: 32, eye: 32 };

/* Tier star count for corner decoration */
const TIER_STARS = { bronze: 1, silver: 2, gold: 3, platinum: 4, diamond: 5 };

function TierDots({ tier }) {
    const count = TIER_STARS[tier] || 0;
    const t = TIERS[tier];
    if (!t || count === 0) return null;

    // Render small dots at the bottom of the badge
    const dotR = 2;
    const gap = 6;
    const totalW = (count - 1) * gap;
    const startX = 32 - totalW / 2;

    return (
        <g>
            {Array.from({ length: count }, (_, i) => (
                <circle
                    key={i}
                    cx={startX + i * gap}
                    cy={60}
                    r={dotR}
                    fill={t.star}
                    opacity="0.9"
                />
            ))}
        </g>
    );
}

const BadgeArt = ({ badgeId, emoji, locked }) => {
    const meta = BADGE_META[badgeId];
    if (!meta) {
        return <span className="badge-art-fallback">{locked ? '\u{1F512}' : emoji}</span>;
    }

    const tier = TIERS[meta.tier] || TIERS.bronze;
    const fill = locked ? LOCKED_FILL : meta;
    const rim = locked ? LOCKED_RIM : tier;
    const gid = 'ba-' + badgeId.replace(/[^a-zA-Z0-9]/g, '') + (locked ? 'L' : '');
    const rid = gid + 'R';
    const ShapeComp = SHAPE_MAP[meta.shape];
    const ty = TEXT_Y[meta.shape] || 32;

    return (
        <svg
            className={`badge-art ${locked ? '' : 'badge-art--earned'}`}
            viewBox="0 0 64 64"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label={badgeId}
        >
            <defs>
                {/* Fill gradient */}
                <linearGradient id={gid} x1="0.2" y1="0" x2="0.8" y2="1">
                    <stop offset="0%" stopColor={fill.g1}/>
                    <stop offset="100%" stopColor={fill.g2}/>
                </linearGradient>
                {/* Rim gradient (metallic tier color) */}
                <linearGradient id={rid} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={rim.r1}/>
                    <stop offset="40%" stopColor={rim.r1} stopOpacity="0.9"/>
                    <stop offset="60%" stopColor={rim.r2} stopOpacity="0.8"/>
                    <stop offset="100%" stopColor={rim.r2}/>
                </linearGradient>
            </defs>

            {/* Badge shape */}
            <ShapeComp gid={gid} rid={rid}/>

            {/* Tier dots (only when earned) */}
            {!locked && <TierDots tier={meta.tier}/>}

            {/* Center emoji */}
            <text
                x="32"
                y={ty}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="20"
            >
                {locked ? '\u{1F512}' : emoji}
            </text>
        </svg>
    );
};

export default BadgeArt;
